import { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import api from "../../Api";
import { GET_SMART_VEHICLE_DATA } from "../realQueries";
import { EventType, TrackingWindow, Vehicle } from "../types";

// Real event Key values, per eventKeysMap in the legacy VehicleTracking/TelematicsView.
const EVENT_KEY_MAP: Record<string, EventType> = {
  speeding: "speeding",
  hardCornering: "hardCornering",
  harshAcceleration: "harshAcceleration",
  harshBraking: "harshBraking",
  lostGSM: "lostGSM",
  lostGPS: "lostGPS",
};

function parseLatLong(value: string): [number, number] | null {
  const [latStr, lngStr] = value.split(",");
  const lat = parseFloat(latStr);
  const lng = parseFloat(lngStr);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  return [lat, lng];
}

export interface PeriodStats {
  distance: string;
  engineHours: string;
  fuelUsage: string;
  stoppageHours: string;
  latestOdometer?: string;
  latestFuelLevel?: number;
  address?: string;
}

// "Latest" fields (odometer, fuel level, address) describe the device's current state and
// don't depend on a tracking window — they're fetched as soon as a device-having vehicle is
// selected. The tracking window's route/events/period-summary DO depend on `hours`, and stay
// gated behind the user explicitly picking one from TrackingHistoryBar; `hours` here is only a
// query-param default so the one shared API call can still return a route when a window is
// picked, without re-fetching just for the latest-reading fields.
const DEFAULT_HOURS_FOR_LATEST_READING = 24;

// Tracking history is only available with driving events for CalAmp/legacy (ESN)
// vehicles via getTelematicsMapData. Teltonika/Smartcar-only vehicles expose a
// bare GPS trail (no event flags) via getSmartCarDataForVehicleId.
export function useVehicleTracking(vehicle: Vehicle | undefined, hours: number | null) {
  const [window, setWindow] = useState<TrackingWindow | undefined>(undefined);
  const [stats, setStats] = useState<PeriodStats | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  const [fetchSmartCarRoute] = useLazyQuery(GET_SMART_VEHICLE_DATA, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const routesData = data?.getSmartCarDataForVehicleId?.mapRoutesData;
      const latlongs: string[] = routesData?.latlong ?? [];
      const route = latlongs.map(parseLatLong).filter((p): p is [number, number] => p !== null);
      setWindow(route.length ? { route, events: [] } : undefined);
      setUnavailable(route.length === 0);
      setLoading(false);
    },
    onError: () => {
      setWindow(undefined);
      setUnavailable(true);
      setLoading(false);
    },
  });

  useEffect(() => {
    if (!vehicle) {
      setWindow(undefined);
      setUnavailable(false);
      setStats(undefined);
      return;
    }

    // A tracking window was explicitly picked — the route/events/unavailable state below is
    // only meaningful (and only rendered by LiveMap) when this is true.
    const wantsRoute = !!hours;

    if (!vehicle.telemetryDeviceId && !wantsRoute) {
      // Smartcar-only vehicle with no tracking window picked — there's no "latest reading"
      // endpoint for Smartcar, so there's nothing to fetch; the panel falls back to the
      // list-level vehicle.fuel/vehicle.odometer/vehicle.location for these.
      setStats(undefined);
      setWindow(undefined);
      setUnavailable(false);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    if (wantsRoute) setUnavailable(false);

    if (vehicle.telemetryDeviceId) {
      api
        .getTelematicsMapData({ esn: vehicle.telemetryDeviceId, hours: hours ?? DEFAULT_HOURS_FOR_LATEST_READING })
        .then(({ data: axiosData }: { data: any }) => {
          if (cancelled) return;

          if (wantsRoute) {
            const routeLatLongs: string[] = axiosData?.mapRoutesData?.latlong ?? [];
            const route = routeLatLongs.map(parseLatLong).filter((p): p is [number, number] => p !== null);

            const flags = axiosData?.mapFlagsData;
            const events = (flags?.latLong ?? flags?.latlong ?? []).reduce((acc: TrackingWindow["events"], latLong: string, i: number) => {
              const point = parseLatLong(latLong);
              const eventType = EVENT_KEY_MAP[flags?.Key?.[i]];
              if (!point || !eventType) return acc;
              const rawTime = flags?.eventTime?.[i];
              const ts = rawTime ? new Date(rawTime) : null;
              acc.push({
                type: eventType,
                lat: point[0],
                lng: point[1],
                date: ts ? ts.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "",
                time: ts ? ts.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "",
                value: flags?.magnitude?.[i] ? `${flags.magnitude[i]}` : undefined,
              });
              return acc;
            }, []);

            setWindow(route.length ? { route, events } : undefined);
            setUnavailable(route.length === 0);
          }

          const distanceNum = axiosData?.milesDriven != null ? Number(axiosData.milesDriven) : NaN;
          const engineHoursNum = axiosData?.engineHours != null ? parseFloat(axiosData.engineHours) : NaN;
          // Backend can send the literal string "N/A" (or a NaN-producing value) when fuel data
          // isn't available for this device — Number("N/A").toFixed(2) previously rendered the
          // ungainly "NaN L" instead of a clear "no data" message.
          const fuelUsageNum = axiosData?.fuelUsage != null ? Number(axiosData.fuelUsage) : NaN;
          const stoppageHoursNum = axiosData?.stoppageHours != null ? parseFloat(axiosData.stoppageHours) : NaN;
          // latestOdometer comes back already formatted in this vehicle's own branch distance
          // unit (see getLatestTelematicsReadings/getBranchDistanceUnitForDeviceEsn in
          // notification-api) — no client-side unit conversion needed here.
          const latestOdometer = typeof axiosData?.latestOdometer === "string" ? axiosData.latestOdometer : undefined;
          // latestFuelLevel is "0" both for a genuinely empty tank and for any device with no
          // CAN fuel telemetry (GPS-only Teltonika, or any CalAmp — see getLatestTelematicsReadings
          // in notification-api) — the backend doesn't distinguish the two, so this can't either.
          const latestFuelLevelNum = axiosData?.latestFuelLevel != null ? Number(axiosData.latestFuelLevel) : NaN;
          // lastLocation comes back as the literal string "not available" (mi-dashboard-service)
          // when there's nothing to report, rather than an absent field.
          const address =
            typeof axiosData?.lastLocation === "string" &&
            axiosData.lastLocation.trim() !== "" &&
            axiosData.lastLocation.toLowerCase() !== "not available"
              ? axiosData.lastLocation
              : undefined;

          setStats({
            distance: !Number.isNaN(distanceNum) ? `${distanceNum.toFixed(1)} mi` : "—",
            engineHours: !Number.isNaN(engineHoursNum) ? `${engineHoursNum.toFixed(1)} hrs` : "—",
            fuelUsage: !Number.isNaN(fuelUsageNum) ? `${fuelUsageNum.toFixed(2)} L` : "N/A",
            stoppageHours: !Number.isNaN(stoppageHoursNum) ? `${stoppageHoursNum.toFixed(1)} hrs` : "—",
            latestOdometer,
            latestFuelLevel: !Number.isNaN(latestFuelLevelNum) ? Math.round(latestFuelLevelNum) : undefined,
            address,
          });
          setLoading(false);
        })
        .catch(() => {
          if (cancelled) return;
          setStats(undefined);
          if (wantsRoute) {
            setWindow(undefined);
            setUnavailable(true);
          }
          setLoading(false);
        });
    } else {
      setStats(undefined);
      fetchSmartCarRoute({ variables: { vehicleId: vehicle.id, trackingHours: hours } });
    }

    return () => {
      cancelled = true;
    };
  }, [vehicle?.id, vehicle?.telemetryDeviceId, hours]);

  return { trackingWindow: window, stats, loading, unavailable };
}
